/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import { MEDIA_QUERY_FOR_PC } from "constants/mediaQueryForPc";
import { useI18n, I18nTrans } from "hooks/i18n/i18n";
import { TRANSLATE_THIS_BLOCK_BY_WEGLOT } from "i18n/weglot";
import { firestoreActions } from "modules/firestore";
import { modalActions } from "modules/modal";
import { streamingActions } from "modules/streaming";
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { useSelector } from "store";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { TextLink } from "ui/TextLink";
import { convertMapToValues } from "utility";
import { changeStyleWithHosting } from "utility/hosting";

import { TicketCard } from "./TicketCard";
import { useBonusGroups } from "./useBonusGroups";
import { startDatetimeAndEndDatetime } from "./utils";

import type { TicketData } from "@spwn/types/firebase/database";
import type {
  ChosenDigitalAssets,
  Place,
  TicketOptionalInputData,
} from "@spwn/types/firebase/firestore";
import type { EventInfo } from "utility";

type OptionalInputData = {
  [key: string]: {
    id: string;
    isRequired: boolean;
    caption: string;
    priority: number;
    inputText: string;
  };
};

type Props = {
  displayTickets: TicketData[];
  chosenTicketId: string;
  eventInfo: EventInfo;
  placeMap: Record<string, Place>;
  selectNum: React.ReactElement;
  questionnaire?: React.ReactElement;
  ticketQuantity: number;
  chosenDigitalAssets: ChosenDigitalAssets;
  setChosenDigitalAssets: (chosenDigitalAssets: ChosenDigitalAssets) => void;
  handleAddToCart: () => void;
  optionalInputData: OptionalInputData | null;
  ticketOptionalInputData?: TicketOptionalInputData;
  isRequiredTicketOptionalInputField: boolean | undefined;
};

const Breadcrumb = ({
  eventInfo,
  ticket,
}: {
  eventInfo: EventInfo;
  ticket: TicketData;
}) => {
  const { t } = useI18n();

  return (
    <BreadcrumbNavigation
      breadcrumbs={[
        { label: t("common.header.nav.home"), link: "/" },
        {
          label: changeStyleWithHosting().commonSettings.menu.event.text,
          link: "/events",
        },
        { label: eventInfo.eventTitle, link: `/events/${eventInfo.eventId}` },
        {
          label: ticket.name,
          link: null,
        },
      ]}
      overWriteAccessColor="black"
    />
  );
};
const addToCartIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M15.55 13C16.3 13 16.96 12.59 17.3 11.97L20.88 5.48C20.9643 5.32843 21.0075 5.15747 21.0054 4.98406C21.0034 4.81064 20.956 4.64077 20.8681 4.49126C20.7803 4.34175 20.6549 4.21778 20.5043 4.13162C20.3538 4.04545 20.1834 4.00009 20.01 4H5.21L4.27 2H1V4H3L6.6 11.59L5.25 14.03C4.52 15.37 5.48 17 7 17H19V15H7L8.1 13H15.55ZM6.16 6H18.31L15.55 11H8.53L6.16 6ZM7 18C5.9 18 5.01 18.9 5.01 20C5.01 21.1 5.9 22 7 22C8.1 22 9 21.1 9 20C9 18.9 8.1 18 7 18ZM17 18C15.9 18 15.01 18.9 15.01 20C15.01 21.1 15.9 22 17 22C18.1 22 19 21.1 19 20C19 18.9 18.1 18 17 18Z"
        fill="white"
      />
    </svg>
  );
};

export const EventTicketDetail: React.FC<Props> = ({
  displayTickets,
  chosenTicketId,
  eventInfo,
  placeMap,
  selectNum,
  questionnaire,
  ticketQuantity,
  chosenDigitalAssets,
  setChosenDigitalAssets,
  handleAddToCart,
  optionalInputData,
  ticketOptionalInputData,
  isRequiredTicketOptionalInputField,
}) => {
  const classes = styles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, i18n } = useI18n();
  const currentLanguage = i18n.language;

  const chosenTicket = displayTickets.find(
    (ticket) => ticket._id === chosenTicketId
  );

  // カスタムフックでボーナスグループを取得
  const {
    bonusGroupsWithMedia,
    isLoading: isLoadingBonusGroups,
    error: bonusGroupsError,
  } = useBonusGroups({
    eventId: eventInfo.eventId,
    ticketId: chosenTicketId,
    enabled: chosenTicket !== undefined,
  });

  const eventVideoMap = useSelector((state) => state.streaming.eventVideoMap);

  // ボーナスグループ取得エラー時にモーダルを表示
  useEffect(() => {
    if (bonusGroupsError !== undefined) {
      dispatch(
        modalActions.toggleError({
          caption: t("settlementHistory.errorCaption"),
          msg: t("event.ticket.errors.bonusGroupLoadFailed"),
        })
      );
    }
  }, [bonusGroupsError, dispatch, t]);

  useEffect(() => {
    dispatch(
      streamingActions.watchEventVideos.started({
        eventId: eventInfo.eventId,
      })
    );
    return () => {
      dispatch(streamingActions.clearStateByKey("eventVideoMap"));
      dispatch(firestoreActions.closeChannel({ channel: "eventVideos" }));
    };
  }, [eventInfo.eventId, dispatch]);

  /**
   * legacy/packages/portal/src/containers/event/eventTicket/EventTicketSelectNum.tsx
   * のconst isPurchased = ticket.remaining === 0;を参考
   */

  const isPurchased = chosenTicket ? chosenTicket.remaining === 0 : false;
  const isSoldOut = chosenTicket ? chosenTicket.remaining === -1 : false;

  // チケットに紐づく動画を取得
  const ticketVideos = useMemo(() => {
    if (!chosenTicket?.videoIds || !eventVideoMap) {
      return [];
    }
    const allVideos = convertMapToValues(eventVideoMap);
    return allVideos
      .filter(
        (video) =>
          video._id !== undefined && chosenTicket.videoIds?.includes(video._id)
      )
      .sort((a, b) => a.priority - b.priority);
  }, [chosenTicket, eventVideoMap]);

  const handleBonusChange = (groupId: string, bonusId: string) => {
    // グループ情報と特典情報を取得
    const group = bonusGroupsWithMedia.find(
      (item) => item.group._id === groupId
    );
    const media = group?.mediaList.find((m) => m._id === bonusId);

    if (media === undefined || group === undefined) {
      console.warn("Invalid groupId or bonusId selected", { groupId, bonusId });
      return;
    }

    const newSelectedBonusIds: ChosenDigitalAssets = {
      ...chosenDigitalAssets,
      [groupId]: {
        [bonusId]: {
          digitalAssetGroupId: groupId,
          digitalAssetId: bonusId,
          digitalAssetName: media.bonusName,
          groupName: group.group.groupName,
        },
      },
    };
    setChosenDigitalAssets(newSelectedBonusIds);
  };

  const isAddToCartDisabled = (() => {
    // 条件1: チケットの選択枚数が1以上
    const hasTicketSelected = ticketQuantity >= 1;
    // 条件2: 全てのbonusGroupについて、どのbonusMediaが選択されているか決まっている
    const allBonusGroupsSelected =
      bonusGroupsWithMedia.length === 0 ||
      bonusGroupsWithMedia.every((item) => {
        const selectedBonus = chosenDigitalAssets[item.group._id];
        return selectedBonus && Object.values(selectedBonus).length > 0;
      });

    // 条件3: 必須アンケートが全て入力されているかチェック
    const allRequiredQuestionsAnswered =
      isRequiredTicketOptionalInputField &&
      ticketOptionalInputData !== undefined
        ? Object.entries(ticketOptionalInputData).every(
            ([key, fieldConfig]) => {
              if (!fieldConfig.isRequired) {
                return true; // 任意項目はスキップ
              }
              if (!optionalInputData) {
                return false; // optionalInputData自体が未定義なら不正
              }

              const inputData = optionalInputData[key];
              return (
                inputData &&
                inputData.inputText &&
                inputData.inputText.trim() !== ""
              );
            }
          )
        : true;

    return !(
      hasTicketSelected &&
      allBonusGroupsSelected &&
      allRequiredQuestionsAnswered &&
      !isPurchased &&
      !isSoldOut &&
      !isLoadingBonusGroups &&
      bonusGroupsError === undefined
    );
  })();

  if (chosenTicket === undefined || isLoadingBonusGroups) {
    return <LoaderLayout />;
  }

  const getAddToCartButtonLabel = () => {
    if (isPurchased) {
      return t("event.archiveVideo.purchased");
    }
    if (isSoldOut) {
      return t("goods.outOfStockError");
    }
    return t("event.ticket.buttons.addToCart2");
  };

  return (
    <div css={classes.root}>
      <Breadcrumb eventInfo={eventInfo} ticket={chosenTicket} />
      <div css={classes.container}>
        <div css={classes.leftColumn}>
          <img
            css={classes.thumbnail}
            src={eventInfo.thumbnail}
            alt={eventInfo.eventTitle}
          />
          <h2 css={classes.ticketName}>{chosenTicket.name}</h2>
          <div css={classes.ticketDescription}>
            <h3 css={classes.ticketDescriptionTitle}>
              {t("event.saleInfo.ticket.ticketSalesPeriod")}
            </h3>
            <p css={classes.ticketDescriptionBody}>
              {startDatetimeAndEndDatetime(
                chosenTicket.releaseDateTime,
                chosenTicket.closeDateTime,
                currentLanguage
              )}
            </p>
          </div>

          {ticketVideos.length > 0 && (
            <div css={classes.ticketDescription}>
              <h3 css={classes.ticketDescriptionTitle}>
                {t("event.saleInfo.ticket.availableStreams")}
              </h3>
              <ul css={classes.ticketDescriptionBody}>
                {ticketVideos.map((video) => (
                  <li key={video._id} css={classes.videoListItem}>
                    {video.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div
            css={classes.textSmallLineHeight5Medium}
            className={TRANSLATE_THIS_BLOCK_BY_WEGLOT}
            dangerouslySetInnerHTML={{ __html: chosenTicket.description }}
          />
        </div>
        <div css={classes.rightColumn}>
          <div css={classes.rightColumnRow}>
            <h3 css={classes.bonusSectionTitle}>{t("event.headline.place")}</h3>
            <p css={classes.bonusGroup}>
              {placeMap[chosenTicket.place]?.name ?? chosenTicket.place}
            </p>
          </div>
          {bonusGroupsWithMedia.length > 0 && (
            <div css={classes.bonusSection}>
              <label css={classes.bonusSectionTitle}>
                {t("event.ticket.headline.bonus")}
              </label>
              {bonusGroupsWithMedia.map((groupWithMedia) => {
                const selectedBonusGroup =
                  chosenDigitalAssets[groupWithMedia.group._id];

                const selectedBonusId =
                  Object.entries(selectedBonusGroup ?? {}).map(
                    ([_, v]) => v
                  )?.[0]?.digitalAssetId ?? "";

                const selectId = `bonus-select-${groupWithMedia.group._id}`;

                return (
                  <div key={groupWithMedia.group._id} css={classes.bonusGroup}>
                    <label htmlFor={selectId} css={classes.bonusLabel}>
                      {groupWithMedia.group.groupName}
                    </label>
                    <select
                      id={selectId}
                      css={classes.bonusSelect}
                      value={selectedBonusId}
                      onChange={(e) =>
                        handleBonusChange(
                          groupWithMedia.group._id,
                          e.target.value
                        )
                      }
                      autoComplete="off"
                    >
                      <option value="">{t("event.ticket.pleaseSelect")}</option>
                      {groupWithMedia.mediaList.map((media) => (
                        <option key={media._id} value={media._id}>
                          {media.bonusName}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          <div css={classes.rightColumnRow}>{selectNum}</div>

          {questionnaire !== undefined && (
            <div css={classes.rightColumnRow}>{questionnaire}</div>
          )}

          <div css={classes.cautionSection}>
            <p css={classes.cautionText}>
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              <I18nTrans i18nKey="event.ticket.cautions.streamingTest">
                ※ライブ配信チケットは
                <TextLink href={`/streaming-test`}>
                  {t("event.ticket.cautions.this")}
                  {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                </TextLink>
                のページで視聴できることをご確認のうえ、ご購入ください。
              </I18nTrans>

              <br />
              {t("event.ticket.cautions.cartExpiration", {
                cartExpiratonMinutes: 15,
              })}
            </p>
          </div>

          <div css={classes.buttonContainer}>
            <button
              css={classes.addToCartButton}
              onClick={handleAddToCart}
              disabled={isAddToCartDisabled}
            >
              {addToCartIcon()} {getAddToCartButtonLabel()}
            </button>
          </div>
        </div>
      </div>
      <div css={classes.otherTicketsSection}>
        <h2 css={classes.otherTicketsTitle}>
          {t("event.ticket.otherTickets")}
        </h2>
        <div css={classes.ticketCardsContainer}>
          {displayTickets
            .filter((ticket) => ticket._id !== chosenTicketId)
            .map((ticket) => (
              <TicketCard
                key={ticket._id}
                ticket={ticket}
                onPurchaseClick={(ticketId) => {
                  history.push(
                    `/events/${eventInfo.eventId}/tickets/${ticketId}`
                  );
                }}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

const styles = () => {
  return {
    root: css`
      background-color: #fff;
      margin: 0 auto;
    `,
    container: css`
      padding: 24px 20px 48px;
      margin: 0 auto;
      max-width: 980px;
      display: flex;
      width: 100%;
      gap: 48px;
      flex-direction: column;
      ${MEDIA_QUERY_FOR_PC} {
        flex-direction: row;
        padding: 40px 0 0;
        gap: 40px;
        width: 80%;
      }
    `,
    leftColumn: css`
      flex: 0 0 380px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      ${MEDIA_QUERY_FOR_PC} {
        gap: 24px;
      }
    `,
    thumbnail: css`
      width: 100%;
      height: auto;
      display: block;
    `,
    rightColumn: css`
      flex: 1;
    `,
    rightColumnRow: css`
      margin-bottom: 48px;
      ${MEDIA_QUERY_FOR_PC} {
        gap: 64px;
      }
    `,
    ticketName: css`
      font-size: 20px;
      font-weight: bold;
      color: #333;
    `,
    textSmallLineHeight5Medium: css`
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      color: black;
    `,
    ticketDescription: css`
      box-shadow: 0 1px 0 0 #d4d4d4;
      padding-bottom: 16px;
    `,
    ticketDescriptionTitle: css`
      font-size: 16px;
      font-weight: bold;
      line-height: 24px;
      color: black;
      margin-bottom: 4px;
    `,
    ticketDescriptionBody: css`
      font-size: 16px;
      line-height: 24px;
      color: #1e1e1e;
    `,
    videoSection: css`
      margin-bottom: 1.5rem;
    `,
    videoSectionTitle: css`
      font-size: 14px;
      font-weight: bold;
      color: #666;
      margin-bottom: 0.5rem;
    `,
    videoList: css`
      list-style: none;
      padding: 0;
      margin: 0;
    `,
    videoListItem: css`
      font-size: 14px;
      color: #333;
      padding: 0.25rem 0;
      &:before {
        content: "・";
        margin-right: 0.25rem;
      }
    `,
    bonusSection: css`
      margin: 24px 0 48px;
      border-radius: 8px;
      ${MEDIA_QUERY_FOR_PC} {
        margin-bottom: 64px;
      }
    `,
    bonusSectionTitle: css`
      margin-bottom: 8px;
      font-size: 18px;
      font-weight: bold;
      color: black;
    `,
    bonusGroup: css`
      padding: 16px 0;
    `,
    bonusLabel: css`
      display: block;
      margin-bottom: 8px;
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
      color: black;
    `,
    bonusSelect: css`
      width: 100%;
      padding: 16px;
      font-size: 16px;
      font-weight: 600;
      border: 1px solid #d9d9d9;
      border-radius: 8px;
      background-color: #fff;
      background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8"><path fill="%23333" d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z"/></svg>');
      background-repeat: no-repeat;
      background-position: right 12px center;
      background-size: 12px;
      cursor: pointer;
      appearance: none;
      &:focus {
        outline: none;
        border-color: #0066cc;
      }
    `,
    cautionSection: css`
      text-align: center;
      margin-bottom: 40px;
    `,
    cautionText: css`
      font-size: 12px;
      color: #e53e3e;
      line-height: 16px;
    `,
    buttonContainer: css`
      display: flex;
      justify-content: center;
      align-items: center;
      ${MEDIA_QUERY_FOR_PC} {
        margin-bottom: 60px;
      }
    `,
    addToCartButton: css`
      width: 100%;
      padding: 10px 20px;
      font-size: 16px;
      height: 48px;
      font-weight: 600;
      color: #fff;
      background-color: black;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      transition: background-color 0.2s;
      &:hover {
        background-color: #333;
      }
      &:active {
        background-color: #000;
      }
      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
      }
      ${MEDIA_QUERY_FOR_PC} {
        width: 240px;
      }
    `,
    otherTicketsSection: css`
      max-width: 980px;
      padding: 0 20px 60px;
      ${MEDIA_QUERY_FOR_PC} {
        margin: 40px auto 64px;
        width: 80%;
        padding: 0px;
        padding-bottom: 40px;
      }
    `,
    otherTicketsTitle: css`
      font-size: 16px;
      font-weight: bold;
      color: black;
      margin-bottom: 19px;
      margin-top: 19px;
      ${MEDIA_QUERY_FOR_PC} {
        margin-bottom: 24px;
        margin-top: 0px;
        font-size: 24px;
      }
    `,
    ticketCardsContainer: css`
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 24px;
    `,
  };
};
